import { Loader2 } from "lucide-react";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { CommentCard } from "@/features/study-group-stream/components/CommentCard";
import {
  CommentThread,
  type CommentWithVisibility,
} from "@/features/study-group-stream/components/CommentThread";
import { useCommentHighlighting } from "@/features/study-group-stream/hooks/useCommentHighlighting";
import {
  type UseCommentSectionProps,
  useCommentSection,
} from "@/features/study-group-stream/hooks/useCommentSection";
import { useCreatedCommentScroll } from "@/features/study-group-stream/hooks/useCommentSectionUiEffects";
import { useCommentSectionViewState } from "@/features/study-group-stream/hooks/useCommentSectionViewState";
import type { StreamActivity } from "@/features/study-group-stream/types/streamActivity";
import {
  COMMENT_LIST_GRID_CLASS,
  LINK_BUTTON_CLASS,
  REPLY_INPUT_ALIGN_CLASS,
} from "@/features/study-group-stream/utils/commentSectionLayout";
import type { CommentExpansionState } from "@/features/study-group-view-state/useStreamViewCapture";
import { useConfirmDeleteDialog } from "@/hooks/useConfirmDeleteDialog";
import { toast } from "@/hooks/useToast";
import { INITIAL_VISIBLE, isUserAuthor } from "../utils/commentSectionUtils";

interface CommentSectionProps extends UseCommentSectionProps {
  onSendMessage?: (userId: number) => void;
  openComposer?: boolean;
  onCommentSent?: () => void;
  onCommentComposerCancel?: () => void;
  highlightCommentId?: number | null;
  isArchived?: boolean;
  highlightCommentIds?: number[];
  highlightActivities?: StreamActivity[];
  shouldScrollToComment?: boolean;
  onHighlightLocated?: (commentId: number) => void;
  onHighlightExhausted?: () => void;
  newCommentIds?: number[];
  newCommentActivities?: StreamActivity[];
  restoredExpansion?: CommentExpansionState;
}

const findVerticalScrollContainer = (element: HTMLElement) => {
  let parent = element.parentElement;
  while (parent) {
    const { overflowY } = window.getComputedStyle(parent);
    if (
      /(auto|scroll)/.test(overflowY) &&
      parent.scrollHeight > parent.clientHeight
    ) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

export function CommentSection({
  postContentId,
  postObjectId,
  studyGroupId,
  selectedTopics,
  currentUser,
  initialComments,
  initialCommentsTotal,
  onSendMessage,
  openComposer = false,
  onCommentSent,
  onCommentComposerCancel,
  highlightCommentId = null,
  isArchived = false,
  highlightCommentIds: providedHighlightCommentIds = [],
  highlightActivities = [],
  shouldScrollToComment = true,
  onHighlightLocated,
  onHighlightExhausted,
  newCommentIds: providedNewCommentIds = [],
  newCommentActivities = [],
  restoredExpansion,
  isRetainedRestoration = false,
  onExpansionChange,
  fetchComments = false,
  canCreateComments = true,
}: CommentSectionProps) {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const viewportAnchorRef = useRef<{
    top: number;
    keepWhileLoading?: boolean;
    scrollContainer: HTMLElement | null;
    getTarget: () => HTMLElement | null;
  } | null>(null);
  const highlightCommentIds = useMemo(() => {
    const candidates =
      providedHighlightCommentIds.length > 0
        ? providedHighlightCommentIds
        : highlightCommentId == null
          ? []
          : [highlightCommentId];
    const uniqueIds: number[] = [];
    const seen = new Set<number>();

    for (const candidate of candidates) {
      const parsed = Number(candidate);
      if (Number.isNaN(parsed) || seen.has(parsed)) continue;
      seen.add(parsed);
      uniqueIds.push(parsed);
    }

    return uniqueIds;
  }, [providedHighlightCommentIds, highlightCommentId]);
  const highlightedCommentIds = useMemo(
    () => new Set(highlightCommentIds),
    [highlightCommentIds],
  );
  const highlightReplyParentById = useMemo(() => {
    const parents = new Map<number, number>();
    for (const activity of highlightActivities) {
      if (activity.type === "reply" && activity.parentCommentId) {
        parents.set(activity.id, activity.parentCommentId);
      }
    }
    return parents;
  }, [highlightActivities]);
  const initialCommentsContainHighlight = useMemo(() => {
    if (!initialComments || highlightCommentIds.length === 0) return false;
    const targetIds = new Set(highlightCommentIds);
    const containsTarget = (comments: typeof initialComments): boolean =>
      comments.some(
        (comment) =>
          targetIds.has(Number(comment.id)) ||
          containsTarget(comment.comments ?? []),
      );
    return containsTarget(initialComments);
  }, [highlightCommentIds, initialComments]);
  const newCommentIds = useMemo(
    () => new Set(providedNewCommentIds.map(Number)),
    [providedNewCommentIds],
  );
  const mentionTopicIds = useMemo(() => {
    return selectedTopics
      ?.map((topic) => Number(topic.id))
      .filter((topicId) => Number.isFinite(topicId) && topicId > 0);
  }, [selectedTopics]);
  const {
    deleteTargetId,
    setDeleteTargetId,
    createdCommentTarget,
    setCreatedComment,
    clearCreatedCommentTarget,
    loadMoreControlsRef,
  } = useCommentSectionViewState();
  const deleteDialog = useConfirmDeleteDialog();

  const {
    comments,
    allComments,
    totalComments,
    hasMoreComments,
    isExpanded,
    isLoadingMoreComments,
    replyCtx,
    composerAutoFocus,
    composerDraftRestorationSettled,
    isSubmitting,
    handleLoadMoreComments,
    handleCollapseComments,
    expandAllComments,
    expandRepliesForComment,
    handleToggleReplies,
    handleReplyClick,
    handleClearReplyCtx,
    handleSubmitComment,
    handleDeleteComment,
    handleEditComment,
    handleReactComment,
    isDeleting,
  } = useCommentSection({
    postContentId,
    postObjectId,
    studyGroupId,
    selectedTopics,
    currentUser,
    initialComments,
    initialCommentsTotal,
    initialIsExpanded: restoredExpansion?.isExpanded,
    initialExpandedReplyIds: restoredExpansion?.expandedReplyIds,
    isRetainedRestoration,
    onExpansionChange,
    fetchComments:
      fetchComments ||
      initialComments === undefined ||
      openComposer ||
      (highlightCommentIds.length > 0 && !initialCommentsContainHighlight),
    mergeInitialCommentsIntoRetainedCache:
      shouldScrollToComment && highlightCommentIds.length > 0,
    canCreateComments,
    onCommentSubmitted: ({ createdCommentId }) => {
      if (createdCommentId) {
        setCreatedComment(createdCommentId);
        onCommentSent?.();
      }
    },
  });
  const hasComments = comments.length > 0;
  const visibleCommentIds = useMemo(() => {
    const ids = new Set<number>();
    for (const comment of comments) {
      ids.add(Number(comment.id));
      for (const reply of comment.visibleReplies) {
        ids.add(Number(reply.id));
      }
    }
    return ids;
  }, [comments]);
  const handleDeleteRequest = useCallback(
    async (id: number) => {
      setDeleteTargetId(id);
      deleteDialog.open();
    },
    [deleteDialog, setDeleteTargetId],
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteTargetId === null) return;
    const id = deleteTargetId;
    try {
      await handleDeleteComment(id);
    } catch (error) {
      console.error("[CommentSection] delete request failed", { id, error });
      toast({ variant: "error", description: t("common.deleteError") });
    } finally {
      setDeleteTargetId(null);
      deleteDialog.close();
    }
  }, [deleteDialog, deleteTargetId, handleDeleteComment, setDeleteTargetId, t]);

  const isReplyMode = Boolean(replyCtx);
  const isTopComposerOpen = openComposer && !replyCtx;

  useEffect(() => {
    if (openComposer && replyCtx) {
      handleClearReplyCtx();
    }
  }, [openComposer, replyCtx, handleClearReplyCtx]);

  useCreatedCommentScroll({
    createdCommentTarget,
    clearCreatedCommentTarget,
    hasMoreComments,
    isLoadingMoreComments,
    handleLoadMoreComments,
  });

  useEffect(() => {
    const releasePosition = () => {
      if (viewportAnchorRef.current?.keepWhileLoading) {
        viewportAnchorRef.current = null;
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (
        [
          "ArrowUp",
          "ArrowDown",
          "PageUp",
          "PageDown",
          "Home",
          "End",
          " ",
        ].includes(event.key)
      ) {
        releasePosition();
      }
    };
    window.addEventListener("wheel", releasePosition, {
      passive: true,
      capture: true,
    });
    window.addEventListener("touchmove", releasePosition, {
      passive: true,
      capture: true,
    });
    window.addEventListener("pointerdown", releasePosition, true);
    window.addEventListener("keydown", handleKeyDown, true);
    return () => {
      window.removeEventListener("wheel", releasePosition, true);
      window.removeEventListener("touchmove", releasePosition, true);
      window.removeEventListener("pointerdown", releasePosition, true);
      window.removeEventListener("keydown", handleKeyDown, true);
    };
  }, []);

  const handleExpandAndPreservePosition = useCallback(() => {
    if (!isExpanded) {
      const commentId = comments[0]?.id;
      const getTarget = () =>
        sectionRef.current?.querySelector<HTMLElement>(
          `[data-comment-id="${commentId}"]`,
        ) ?? null;
      const target = getTarget();
      if (target) {
        viewportAnchorRef.current = {
          top: target.getBoundingClientRect().top,
          scrollContainer: findVerticalScrollContainer(target),
          getTarget,
          keepWhileLoading: true,
        };
      }
    }
    return handleLoadMoreComments();
  }, [comments, handleLoadMoreComments, isExpanded]);

  const handleCollapseAndRevealLoadMore = useCallback(() => {
    const anchorElement = loadMoreControlsRef.current;
    if (anchorElement) {
      viewportAnchorRef.current = {
        top: anchorElement.getBoundingClientRect().top,
        scrollContainer: findVerticalScrollContainer(anchorElement),
        getTarget: () => loadMoreControlsRef.current,
      };
    }
    handleCollapseComments();
  }, [handleCollapseComments, loadMoreControlsRef]);

  const handleToggleRepliesAndRevealShowMore = useCallback(
    (commentId: number, areRepliesExpanded: boolean) => {
      const selector = `[data-reply-toggle-comment-id="${commentId}"]`;
      const replyId = comments.find(
        (comment) => Number(comment.id) === commentId,
      )?.visibleReplies[0]?.id;
      const targetSelector = areRepliesExpanded
        ? selector
        : `[data-comment-id="${replyId}"]`;
      const getTarget = () =>
        sectionRef.current?.querySelector<HTMLElement>(targetSelector) ?? null;
      const anchorElement = getTarget();
      if (anchorElement) {
        viewportAnchorRef.current = {
          top: anchorElement.getBoundingClientRect().top,
          scrollContainer: findVerticalScrollContainer(anchorElement),
          getTarget,
        };
      }
      handleToggleReplies(commentId);
    },
    [comments, handleToggleReplies],
  );

  useLayoutEffect(() => {
    const anchor = viewportAnchorRef.current;
    if (!anchor) return;
    const target = anchor.getTarget();
    if (!target) {
      if (!isLoadingMoreComments) viewportAnchorRef.current = null;
      return;
    }
    if (!anchor.keepWhileLoading || !isLoadingMoreComments) {
      viewportAnchorRef.current = null;
    }
    const offset = target.getBoundingClientRect().top - anchor.top;
    if (Math.abs(offset) < 1) return;
    if (anchor.scrollContainer) {
      anchor.scrollContainer.scrollTop += offset;
    } else {
      window.scrollBy({ top: offset, behavior: "auto" });
    }
  });

  const renderReplyInput = (
    parentId: number,
    anchorId: number,
    variant: "comment" | "reply",
  ) => {
    if (!canCreateComments || !isReplyMode) return null;
    if (replyCtx?.anchorId !== anchorId) return null;
    const containerClassName =
      variant === "comment"
        ? `mt-2 mb-2 min-w-0 ${REPLY_INPUT_ALIGN_CLASS}`
        : `mt-1 min-w-0 ${REPLY_INPUT_ALIGN_CLASS}`;

    return (
      <div className={containerClassName}>
        <CommentCard
          isInput
          postId={parentId} // parentId in API
          draftPostId={postContentId}
          replyParentCommentId={parentId}
          replyAnchorCommentId={anchorId}
          onAddComment={handleSubmitComment}
          isSubmitting={isSubmitting}
          isReplyInput
          onCancelInput={handleClearReplyCtx}
          autoFocus={replyCtx.autoFocus !== false}
          onDraftRestorationSettled={replyCtx.onDraftRestorationSettled}
          studyGroupId={studyGroupId}
          currentUserId={currentUser?.id}
          topicIds={mentionTopicIds}
        />
      </div>
    );
  };

  useCommentHighlighting({
    highlightCommentIds,
    highlightReplyParentById,
    visibleCommentIds,
    allComments,
    isExpanded,
    hasMoreComments,
    isLoadingMoreComments,
    handleLoadMoreComments,
    expandAllComments,
    expandRepliesForComment,
    shouldScrollToComment,
    onHighlightLocated,
    onHighlightExhausted,
  });

  const hiddenTopLevelCommentsCount = Math.max(
    0,
    totalComments - comments.length,
  );
  const visibleTopLevelCommentIds = new Set(
    comments.map((comment) => Number(comment.id)),
  );
  const hiddenTopLevelUnread = newCommentActivities.filter(
    (activity) =>
      activity.type === "comment" &&
      activity.parentCommentId === null &&
      !visibleTopLevelCommentIds.has(activity.id),
  );
  const hiddenTopLevelMentionCount = hiddenTopLevelUnread.filter(
    (activity) => activity.isMention,
  ).length;
  const loadMoreTopLevelLabel = t("common.showMore");
  const canCollapseComments = isExpanded && comments.length > INITIAL_VISIBLE;

  return (
    <div
      ref={sectionRef}
      className={`flex flex-col px-3 pt-0 ${
        hasComments || isTopComposerOpen ? "pb-3" : "pb-1"
      }`}
    >
      <ConfirmationDialog
        open={deleteDialog.isOpen}
        onOpenChange={(open) => {
          if (!open) {
            deleteDialog.close();
            setDeleteTargetId(null);
          }
        }}
        title={t("common.delete")}
        description={t("common.confirmDelete")}
        confirmText={t("common.delete")}
        cancelText={t("common.cancel")}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        variant="destructive"
      />

      {/* 1. 新規コメント入力 */}
      {!isArchived && canCreateComments && isTopComposerOpen && (
        <div className={`mb-3 ${REPLY_INPUT_ALIGN_CLASS}`}>
          <CommentCard
            isInput
            postId={postObjectId}
            draftPostId={postContentId}
            onAddComment={handleSubmitComment}
            onCancelInput={onCommentComposerCancel}
            isSubmitting={isSubmitting}
            autoFocus={composerAutoFocus}
            onDraftRestorationSettled={composerDraftRestorationSettled}
            studyGroupId={studyGroupId}
            currentUserId={currentUser?.id}
            topicIds={mentionTopicIds}
          />
        </div>
      )}

      {/* 2. コメントリスト */}
      <div
        className={
          hasComments ? `relative z-10 ${COMMENT_LIST_GRID_CLASS}` : "min-w-0"
        }
      >
        {hasComments && <div aria-hidden="true" />}
        <div className="min-w-0">
          {comments.map((comment) => (
            <CommentThread
              key={comment.id}
              comment={comment as CommentWithVisibility}
              replyCtx={replyCtx}
              highlightedCommentIds={highlightedCommentIds}
              newCommentIds={newCommentIds}
              newCommentActivities={newCommentActivities}
              onReaction={handleReactComment}
              onReplyClick={handleReplyClick}
              onDeleteComment={handleDeleteRequest}
              onEditComment={handleEditComment}
              onToggleReplies={handleToggleRepliesAndRevealShowMore}
              isCurrentUserAuthor={(target) =>
                isUserAuthor(target, currentUser?.id)
              }
              currentUserId={currentUser?.id}
              topicIds={mentionTopicIds}
              onSendMessage={onSendMessage}
              renderReplyInput={renderReplyInput}
              renderHiddenRepliesLabel={(count, areRepliesExpanded) =>
                areRepliesExpanded
                  ? t("common.showLess")
                  : `${t("common.showMore")} (${count})`
              }
              isArchived={isArchived}
              canCreateComments={canCreateComments}
            />
          ))}

          {/* 3. トップレベルコメントをさらに表示 */}
          {(hasMoreComments || canCollapseComments) && (
            <div ref={loadMoreControlsRef} className="flex flex-wrap gap-2">
              {hasMoreComments && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={LINK_BUTTON_CLASS}
                  onClick={handleExpandAndPreservePosition}
                  disabled={isLoadingMoreComments}
                >
                  {isLoadingMoreComments ? (
                    <Loader2 className="mr-2 size-4 animate-spin" />
                  ) : (
                    loadMoreTopLevelLabel
                  )}{" "}
                  ({hiddenTopLevelCommentsCount})
                  {hiddenTopLevelMentionCount > 0 ? (
                    <output
                      className="ml-1 inline-flex min-w-5 items-center justify-center rounded-full bg-red-500 px-1.5 text-[11px] font-bold leading-5 text-white"
                      aria-label={`${hiddenTopLevelMentionCount}件の未読メンション`}
                    >
                      {hiddenTopLevelMentionCount}
                    </output>
                  ) : hiddenTopLevelUnread.length > 0 ? (
                    <output
                      className="ml-1 inline-block size-2 rounded-full bg-sky-500"
                      aria-label="未読コメントあり"
                    />
                  ) : null}
                </Button>
              )}
              {canCollapseComments && !isLoadingMoreComments && (
                <Button
                  variant="ghost"
                  size="sm"
                  className={LINK_BUTTON_CLASS}
                  onClick={handleCollapseAndRevealLoadMore}
                >
                  {t("common.showLess")}
                </Button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
